import React, { useCallback, useEffect, useMemo, useState } from 'react';
import styles from './TradeRecommendationTable.module.css';
import Button from '../../common/Button/Button';
import { useRealtimePrices } from '../../../hooks/useRealtimePrices';
import { subscribeSymbols, unsubscribeDelayed } from '../../../ws/marketSubscriptions';
import api from '../../../services/api';
import { userService } from '../../../services/userService';
import { formatAdminDate } from '../../../utils/datetime';

const EditIcon = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M12 20h9" />
    <path d="M16.5 3.5a2.12 2.12 0 1 1 3 3L7 19l-4 1 1-4Z" />
  </svg>
);

const TrashIcon = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
    <polyline points="3 6 5 6 21 6" />
    <path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6" />
    <path d="M10 11v6" />
    <path d="M14 11v6" />
    <path d="M9 6V4h6v2" />
  </svg>
); const TradeRecommendationTable = ({ tradeRecommendations, onEdit, onDelete, onView, canEdit, canCreate, canDelete }) => {


  const { prices: realtimePrices } = useRealtimePrices();

  // ===== Fetch usernames for all unique userIds =====
  const [userNames, setUserNames] = useState({});
  const uniqueUserIds = useMemo(() => {
    return [...new Set(
      tradeRecommendations
        .map(t => t.userId)
        .filter(id => id != null && id !== '' && id !== undefined)
    )];
  }, [tradeRecommendations]);

  useEffect(() => {
    if (uniqueUserIds.length === 0) return;
    let cancelled = false;

    const fetchNames = async () => {
      const newNames = {};
      const idsToFetch = uniqueUserIds.filter(id => !userNames[id]);
      if (idsToFetch.length === 0) return;

      await Promise.allSettled(
        idsToFetch.map(async (id) => {
          try {
            const response = await userService.getUserById(id);
            const data = response.data?.data || response.data || response;
            newNames[id] = data.name || data.username || data.email || '-';
          } catch (err) {
            newNames[id] = '-';
          }
        })
      );

      if (!cancelled) {
        setUserNames(prev => ({ ...prev, ...newNames }));
      }
    };

    fetchNames();
    return () => { cancelled = true; };
  }, [uniqueUserIds]);

  const mergeWithRealtime = (list, realtimePrices) => {
    return list.map(item => {
      const isLive = String(item.status || "").toLowerCase() === "live";
      const rt = realtimePrices[item.token];

      // ✅ LTP: Only use realtime for Live trades, otherwise use stored ltp
      const ltp = isLive && rt?.price != null
        ? Number(rt.price)
        : Number(item.ltp || item.value || 0);

      // ✅ Prev Close: Only use realtime for Live trades
      const prevClose = isLive && rt?.prevClose != null
        ? Number(rt.prevClose)
        : Number(item.prev_close || item.prevClose || ltp);

      // ✅ Recalculate every time — fresh & consistent
      const change = ltp - prevClose;
      const changePercent = prevClose !== 0 ? (change / prevClose) * 100 : 0;

      return {
        ...item,
        value: ltp,
        prevClose,
        change,
        changePercent,
        timestamp: rt?.timestamp || item.timestamp || new Date().toISOString(),
      };
    });
  };

  // Get all unique, valid script symbols
  const scriptsToSubscribe = useMemo(() => {
    return [...new Set(
      tradeRecommendations
        .map(trade => trade.script || trade.script_id)
        .filter(Boolean) // remove null/undefined
        .map(s => s.toUpperCase().trim())
    )];
  }, [tradeRecommendations]);

  useEffect(() => {
    if (scriptsToSubscribe.length === 0) return;

    const page = "TradeCard";
    const context = "TradeList";

    subscribeSymbols(scriptsToSubscribe, page, context);

    // Cleanup on unmount or when scripts change
    return () => {
      unsubscribeDelayed(scriptsToSubscribe, page, context);
    };
  }, [scriptsToSubscribe]);

  // const displayStocks = mergeWithRealtime(tradeRecommendations, realtimePrices)

  const displayStocks = useMemo(() => {
    return mergeWithRealtime(tradeRecommendations, realtimePrices);
  }, [tradeRecommendations, realtimePrices]);

  const handleUpdateStatus = async (tradeId, tradeStatus, exitType, ltp) => {
    try {
      await api.put('/traderecommendation/update-status', { tradeId, tradeStatus, exitType, ltp })

    } catch (error) {
      console.log("Error updating", error);
    }
  };

  // Validate recommendations prop
  if (!tradeRecommendations) {
    console.error('TradeRecommendationTable: tradeRecommendations prop is undefined or null');
    return (
      <div className={styles.tableContainer}>
        <div className={styles.errorState}>
          <div className={styles.errorIcon}>⚠️</div>
          <p>Error: No trade recommendation data provided to table</p>
        </div>
      </div>
    );
  }

  if (!Array.isArray(tradeRecommendations)) {
    console.error('TradeRecommendationTable: tradeRecommendations prop is not an array', tradeRecommendations);
    return (
      <div className={styles.tableContainer}>
        <div className={styles.errorState}>
          <div className={styles.errorIcon}>⚠️</div>
          <p>Error: Trade recommendations data should be an array</p>
        </div>
      </div>
    );
  }

  if (tradeRecommendations.length === 0) {
    return (
      <div className={styles.tableContainer}>
        <div className={styles.emptyState}>
          <div className={styles.emptyIcon}>📊</div>
          <h3>No recommendations to display</h3>
          <p>There are no trade recommendations available at the moment.</p>
        </div>
      </div>
    );
  }


  const formatPrice = (price) => {
    if (!price) return 'N/A';
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(price);
  };

  const toNumber = (value) => {
    const num = Number(value);
    return Number.isFinite(num) ? num : 0;
  };

  const formatDate = (dateString) => formatAdminDate(dateString, '-');


  const calculatePercentage = (entry, target) => {
    if (!entry || !target) return null;
    const percent = ((target - entry) / entry) * 100;
    return percent.toFixed(2);
  };

  const calculatePotentialProfit = (entry, target) => {
    if (!entry || !target) return null;
    const percent = ((target - entry) / entry) * 100;
    return percent.toFixed(2);
  };

  const calculateNewProfit = (entry, target) => {
    if (!entry || !target) return null;
    const percent = ((target - entry) / entry) * 100;
    return percent.toFixed(2);
  };

  const calculateExcpectedProfit = (ltp, target) => {
    if (!ltp || !target) return null;
    const percent = ((target - ltp) / ltp) * 100
    return percent.toFixed(2)
  }

  const calculateActualProfit = (exit, entry) => {
    if (!entry || !exit) return null;
    const percent = ((exit - entry) / entry) * 100
    return percent.toFixed(2)
  }

  const getDuration = (entryDate, exitDate) => {
    if (!entryDate || !exitDate) return "-";

    const entry = new Date(entryDate);
    const exit = new Date(exitDate);

    const diffMs = exit - entry;

    if (diffMs < 0) return "-";
    if (diffMs === 0) return "0m";


    const diffMinutes = Math.floor(diffMs / (1000 * 60));
    const diffHours = Math.floor(diffMinutes / 60);
    const diffDays = Math.floor(diffHours / 24);

    const remainingHours = diffHours % 24;
    const remainingMinutes = diffMinutes % 60;

    if (diffDays > 0) {
      return `${diffDays}d ${remainingHours}h`;
    }

    if (diffHours > 0) {
      return `${diffHours}h ${remainingMinutes}m`;
    }

    return `${diffMinutes}m`;
  };


  const getStatusClass = (status) => {
    const statusLower = String(status).toLowerCase();
    if (statusLower === 'active' || statusLower === 'open' || statusLower === 'live') return styles.active;
    if (statusLower === 'closed' || statusLower === 'completed') return styles.completed;
    if (statusLower === 'target_hit' || statusLower === 'target hit') return styles.targetHit;
    if (statusLower === 'stop_hit' || statusLower === 'stoploss hit') return styles.stopHit;
    if (statusLower === 'cancelled') return styles.cancelled;
    return styles.pending;
  };

  const getRiskClass = (risk) => {
    const riskLower = String(risk).toLowerCase();
    if (riskLower === 'low') return styles.lowRisk;
    if (riskLower === 'high') return styles.highRisk;
    return styles.mediumRisk;
  };

  const getTypeClass = (type) => {
    const typeLower = String(type).toLowerCase();
    if (typeLower.includes('buy') || typeLower === 'long') return styles.buyType;
    if (typeLower.includes('sell') || typeLower === 'short') return styles.sellType;
    return styles.holdType;
  };


  return (
    <div className={styles.tableContainer}>
      <div className={styles.tableWrapper}>
        <table className={styles.tradeTable}>
          <thead>
            <tr>
              <th className={styles.idHeader}>ID</th>
              <th className={styles.idHeader}>User ID / Name</th>
              <th className={`${styles.stockHeader} ${styles.textCenter}`}>Trade / Duration</th>
              <th className={`${styles.stockHeader} ${styles.textCenter}`}>Stock / Type</th>
              <th className={styles.statusHeader}>Status</th>
              <th className={styles.priceHeader}>Prices (E / T / SL)</th>
              <th className={styles.priceHeader}>Ltp</th>
              <th className={styles.metricsHeader}>Exit Price</th>
              <th className={`${styles.priceHeader} ${styles.textRight}`}>Profits (Pot. / Act.)</th>
              <th className={styles.dateHeader}>Created At</th>
              <th className={styles.dateHeader}>Exit At / Act. Duration</th>
              <th className={styles.dateHeader}>Entry Note</th>
              <th className={styles.dateHeader}>Track Only</th>
              <th className={styles.actionsHeader}>Actions</th>
            </tr>
          </thead>
          <tbody>
            {displayStocks.map((trade) => {
              const {
                tradeId,
                stocksymbol,
                stockSymbol,
                stockname,
                stockName,
                recommendationtype,
                recommendationType,
                entryprice,
                entryPrice,
                targetprice,
                targetPrice,
                stoploss,
                stopLoss,
                timeframe,
                risklevel,
                riskLevel,
                status,
                recommendationstatus,
                createdat,
                createdAt,
                recoPriceLow,
                targetOne,
                script_name,
                script_id,
                scriptTypeName,
                exitDate,
                exitTime,
                entryNote,
                script,
                tradeTypeName,
                recoExitDate,
                recoDate,
                exitPriceLow,
                value,
                potential_profits,
                actual_profit,
                actual_duration,
                tradeRecommendationId,
                track_only,
                userId,
              } = trade;



              const type = recommendationtype || recommendationType || "N/A";
              const entry = entryprice || entryPrice;
              const target = targetprice || targetPrice;
              const stop = stoploss || stopLoss;
              let tradeStatus = status;
              const rawCreated = createdat || createdAt;
              // Fix double-conversion: API returns IST without offset, preventing datetime.js from treating it as UTC
              const created = (typeof rawCreated === 'string' && !rawCreated.match(/[zZ]$|[+-]\d/))
                ? rawCreated.replace(' ', 'T') + '+05:30'
                : rawCreated;

              const profitPercent = calculatePercentage(entry, target);
              const lossPercent = calculatePercentage(entry, stop);
              const riskReward =
                profitPercent && lossPercent
                  ? Math.abs(profitPercent / lossPercent).toFixed(2)
                  : null;

              /* calculations for ltp */
              const symbolKey =
                script?.toUpperCase().trim() || script_id?.toUpperCase().trim();
              const rt = realtimePrices?.[symbolKey];
              {
                /* const ltp = rt?.price ?? value; */
              }
              let ltp;
              if (tradeStatus === "Live") {
                ltp = rt?.price ?? value;
              } else {
                ltp = value;
              }


              var actual_profit_percentage;
              var actual_profit1 = 0;
              // Actual Profit  logic
              if (tradeRecommendationId == 1 && status === "Live") {
                actual_profit1 = ltp - recoPriceLow;
                actual_profit_percentage = ((ltp - recoPriceLow) / recoPriceLow) * 100;
              } else if (tradeRecommendationId == 2 && status === "Live") {
                actual_profit1 = recoPriceLow - ltp;
                actual_profit_percentage = ((recoPriceLow - ltp) / recoPriceLow) * 100;
              } else if (tradeRecommendationId == 1 && status !== "Live") {
                actual_profit1 = exitPriceLow - recoPriceLow;
                actual_profit_percentage = ((exitPriceLow - recoPriceLow) / recoPriceLow) * 100;
              } else if (tradeRecommendationId == 2 && status !== "Live") {
                actual_profit1 = recoPriceLow - exitPriceLow;
                actual_profit_percentage = ((recoPriceLow - exitPriceLow) / recoPriceLow) * 100;
              }

              return (
                <tr key={tradeId} className={styles.tradeRow}>
                  <td className={styles.idCell}>{tradeId}</td>
                  <td className={styles.idCell}>
                    <div className={styles.userIdCol}>
                      <span className={styles.userName}>
                        {userId ? (userNames[userId] || '...') : ''}
                      </span>
                      <span className={styles.userIdText}>
                        {userId || "-"}
                      </span>
                    </div>
                  </td>
                  <td className={styles.stockCell}>
                    <div className={`${styles.stockInfo} ${styles.stockInfoCentered}`}>
                      <div className={tradeRecommendationId === 1 ? styles.tradeTypeBuy : styles.tradeTypeSell}>
                        {tradeRecommendationId === 1 ? "Buy" : "Sell"}
                      </div>
                      <div className={`${styles.tradeId} ${styles.tradeIdPoppins}`}>
                        {tradeTypeName}
                      </div>
                    </div>
                  </td>
                  {/* Stock Info Column */}
                  <td className={styles.stockCell}>
                    <div className={`${styles.stockInfo} ${styles.stockInfoCentered}`}>
                      <div className={styles.stockName}>{script_name}</div>
                      <div className={styles.tradeId}>{script_id}</div>
                      <div className={`${styles.tradeId} ${styles.tradeIdPoppins}`}>{scriptTypeName}</div>
                    </div>
                  </td>

                  {/* Status Column */}
                  <td className={styles.statusCell}>
                    <span
                      className={` ${getStatusClass(status)}`}
                    >
                      {status}
                    </span>
                  </td>

                  {/* Combined Prices Column */}
                  <td className={styles.priceCell}>
                    <div className={styles.stackedPrices}>
                      <div className={styles.priceRow}>
                        <span className={styles.priceLabel}>Entry:</span>
                        <div className={styles.priceValue}>
                          {formatPrice(recoPriceLow)}
                        </div>
                      </div>
                      <div className={styles.priceRow}>
                        <span className={styles.priceLabel}>Target:</span>
                        <div className={`${styles.priceValue} ${styles.target}`}>
                          {formatPrice(targetOne)}
                          {profitPercent && (
                            <span className={styles.percentGain}>
                              +{profitPercent}%
                            </span>
                          )}
                        </div>
                      </div>
                      <div className={styles.priceRow}>
                        <span className={styles.priceLabel}>SL:</span>
                        <div className={`${styles.priceValue} ${styles.stopLoss}`}>
                          {formatPrice(stop)}
                          {lossPercent && (
                            <span className={styles.percentLoss}>
                              -{Math.abs(lossPercent)}%
                            </span>
                          )}
                        </div>
                      </div>
                    </div>
                  </td>

                  <td className={styles.priceCell}>
                    <div className={styles.priceValue}>
                      {formatPrice(ltp)}
                    </div>
                  </td>
                  <td className={styles.priceCell}>
                    <div className={styles.priceValue}>
                      {exitPriceLow && (
                        <span>
                          {exitPriceLow == 0 ? '-' : formatPrice(exitPriceLow)}
                        </span>
                      )}
                    </div>
                  </td>
                  <td className={styles.priceCell}>
                    <div className={`${styles.stackedPrices} ${styles.profitsCol}`}>
                      <div className={`${styles.priceRow} ${styles.priceRowSpaced}`}>
                        <span className={styles.priceLabel}>Pot.:</span>
                        <div className={styles.profitValuesCol}>
                          {potential_profits && (
                            <>
                              <span className={`${styles.priceValue} ${styles.target}`}>{potential_profits}</span>
                              <span className={styles.profitPercentGain}>
                                +{((potential_profits / recoPriceLow) * 100).toFixed(2)}%
                              </span>
                            </>
                          )}
                        </div>
                      </div>
                      <div className={`${styles.priceRow} ${styles.priceRowSpaced}`}>
                        <span className={styles.priceLabel}>Act.:</span>
                        <div className={styles.profitValuesCol}>
                          {actual_profit1 && (
                            <>
                              <span className={`${styles.priceValue} ${actual_profit1.toString().startsWith("-") ? styles.colorred : styles.colorgreen}`}>
                                {actual_profit1.toFixed(2)}
                              </span>
                              <span className={actual_profit1.toString().startsWith("-") ? styles.profitPercentLoss : styles.profitPercentGain}>
                                {actual_profit1.toString().startsWith("-") ? '' : '+'}{actual_profit_percentage.toFixed(2)}%
                              </span>
                            </>
                          )}
                        </div>
                      </div>
                    </div>
                  </td>

                  {/* Created Date Column */}
                  <td className={styles.createdAtCell}>
                    <div className={styles.dateValue}>
                      <span>{formatDate(created)}</span>
                      <span className={styles.timeValue}>
                        {created ? new Date(created).toLocaleTimeString('en-US', { hour12: false }) : ''}
                      </span>
                    </div>
                  </td>
                  <td className={styles.dateCell}>
                    <div className={styles.dateValue}>
                      <span>{formatDate(exitDate)}</span>
                      {exitTime && <span className={styles.timeValue}>{exitTime.substring(0, 8)}</span>}
                      <span className={`${styles.timeValue} ${styles.durationPoppins}`}>{actual_duration}</span>
                    </div>
                  </td>
                  <td className={styles.entryNoteCell} title={entryNote}>
                    <div className={styles.entryNoteValue}>{entryNote}</div>
                  </td>

                  <td className={styles.dateCell}>
                    <div className={styles.dateValue}>{track_only ? 'Yes' : 'No'}</div>
                  </td>



                  {/* Actions Column */}
                  <td className={styles.actionsCell}>
                    <div className={styles.actionButtons}>
                      {canEdit && (
                      <button
                        className={styles.iconBtn}
                        onClick={() => onEdit && onEdit(trade)}
                        title="Edit recommendation"
                      >
                        <EditIcon />
                      </button>
                      )}
                      {canDelete && (
                      <button
                        className={`${styles.iconBtn} ${styles.deleteBtn}`}
                        onClick={() => onDelete && onDelete(trade)}
                        title="Delete recommendation"
                      >
                        <TrashIcon />
                      </button>
                      )}
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default TradeRecommendationTable;
